// contract-error-circuit-breaker.ts
//
// Phase 04 (R-23, D11): nối `retry-policy.ts`'s `checkContractErrorAcrossTickets`
// (0 caller trước phase này, Key Insight C2) vào 1 vòng `run` — contract_error
// trên ≥2 ticket phân biệt (lỗi hệ thống: bảng adapter/planner sai, hoặc
// worker liên tục không ra result hợp lệ) dừng runner thay vì cày tiếp từng
// ticket riêng lẻ. Instance MỘT LẦN cho cả lần `run` (production tự tạo khi
// caller không truyền — supervisor-run-until-idle.ts's `loopInput`), dùng
// chung cho cả đường feature lẫn đường review (breaker ghi ở
// finalizeAttemptAfterRoute, điểm chung của cả hai — cùng chỗ dòng ■ dùng).
//
// Sau review M1 (code-review-phase-04.md): record() ghi MỌI attempt
// contract_error kể cả khi ticket còn nguyên ngân sách retry — 2 ticket độc
// lập mỗi ticket trượt thoáng qua 1 lần (rồi tự qua ở lần thử sau) đã đủ
// halt cả mission. Dogfood đo no_result_json_found (session-level
// contract_error) ~1/7 phiên thật ⇒ halt oan. Sửa: `reset()` xoá tập ticket
// đã ghi khi có 1 attempt THÀNH CÔNG (route "done") xen giữa — lỗi hệ thống
// thật là chuỗi lỗi LIÊN TIẾP không xen thành công; 1 done ở giữa chứng
// minh runner vẫn hoạt động được, không phải bảng adapter/planner hỏng.
//
// V2-0 (spec V2 §4.0, chốt 06/10): worker hỏi lại ĐÚNG câu needs_decision người
// đã trả lời ⇒ journal ghi category `contract_error` (1 counted failure) nhưng
// breaker này KHÔNG tính: đó là lỗi của một worker trên một ticket (đã có
// `max_counted_failures` chặn), không phải lỗi hệ thống nguồn (a)/(b). Cycle
// của ca đó là `verified` + `sessionResult.outcome === "ok"`, nên
// `isContractErrorAttempt` trả false — ĐỪNG đổi nó sang đọc
// `decision.category`. Test pin: needs-decision-repeat-does-not-trip-contract-error-breaker.test.ts.
import type { ContractErrorHaltCheck } from "./retry-policy.js";
import { checkContractErrorAcrossTickets } from "./retry-policy.js";
import type { AttemptCycleResult } from "./supervisor-attempt-cycle.js";
import type { RouteOutcomeResult } from "./supervisor-outcome-router.js";

export interface ContractErrorBreaker {
  /** Ghi nhận 1 ticket vừa có contract_error attempt (nguồn (a) hoặc (b)). */
  record(ticketId: string): void;
  /** Sau review M1: 1 attempt "done" xoá sạch tập ticket đã ghi — lỗi hệ thống thật phải LIÊN TIẾP, không xen thành công. */
  reset(): void;
  /** `undefined` khi chưa chạm ngưỡng; chuỗi lý do khi đã chạm (≥2 ticket phân biệt). */
  haltReason(): string | undefined;
}

/** 1 instance cho MỖI lần `run` — không chia sẻ giữa các lần `run` khác nhau. */
export function createContractErrorBreaker(): ContractErrorBreaker {
  let ticketIds: string[] = [];
  return {
    record(ticketId: string): void {
      ticketIds.push(ticketId);
    },
    reset(): void {
      ticketIds = [];
    },
    haltReason(): string | undefined {
      const check: ContractErrorHaltCheck = checkContractErrorAcrossTickets(ticketIds);
      return check.halt ? check.reason : undefined;
    },
  };
}

/**
 * Nguồn (a): dispatch/adapter không dựng được spec trước spawn. Nguồn (b):
 * phiên chạy xong nhưng không có result hợp lệ (missing_result_event/
 * no_result_json_found/invalid_structured_output_schema). Quota/rate-limit
 * cũng ra `sessionResult.outcome === "contract_error"` nhưng đi đường
 * `quota_sleep` TRƯỚC — loại bằng `route.kind === "retry_decision"` (Key
 * Insight C2, tên/giá trị đo ở `948926c`).
 *
 * KHÔNG gồm needs_decision lặp câu đã trả lời (V2-0): route mang category
 * `contract_error` nhưng cycle là verified/ok — cố ý, xem header file.
 */
export function isContractErrorAttempt(
  cycle: AttemptCycleResult,
  route: RouteOutcomeResult,
): boolean {
  if (route.kind !== "retry_decision") return false;
  if (cycle.kind === "contract_error") return true;
  return cycle.kind === "verified" && cycle.sessionResult.outcome === "contract_error";
}
